import React, { useState } from 'react';
import { Colors } from 'library';
import { FieldGroup, Card, Grid, Link, Row, Select, Stack, Text, TextInput } from 'library/ui';

import { AdditionalPills } from './AdditionalPills';
import { AdditionalModal } from './AdditionalModal';
import { educationChoice, experienceChoice } from '../utils/choices';
import { gatherDisplayPills } from '../utils/pills';
import { updateScale } from '../utils/scale';

import { useRequirementsAndPayContext } from '../context';

import type { IManagedScale, GenericChoice, Pills, Qualification } from '../types';
import { orderBy } from 'shared/arrays';
import { MINIMUM_WAGE_BY_STATE } from '../constants';
import { styled } from '@mui/material';

export function PayRow({ scale, editing }: { scale: IManagedScale; editing?: boolean }) {
    const {
        problems: allProblems,
        scales,
        initialScale,
        qualifications,
        state,
        updateScales,
        deleteScale,
    } = useRequirementsAndPayContext();

    const initialRate = isNaN(scale.rate) ? '0' : parseFloat(scale.rate.toFixed(2));
    const problems = allProblems[scale.id];

    const [rate, setRate] = useState<string>(initialRate.toString());
    const [open, setOpen] = useState<boolean>(false);

    const pills: Pills = gatherDisplayPills(scale, qualifications, state);

    function update(
        value: number | number[],
        choices: GenericChoice[],
        qualification: Qualification | null,
        category: 'education' | 'experience' | 'multiple' | 'single' | 'boolean' | 'rate',
    ) {
        const newScale = updateScale(scale, value, choices, qualification, category);

        updateScales(newScale);
    }

    function coerceRate(rate: string): number {
        const parsedRate = parseFloat(rate);
        if (!isNaN(parsedRate)) {
            const coercedRate = parsedRate.toFixed(2);

            setRate(coercedRate);
            return parseFloat(coercedRate);
        }

        setRate(rate);
        return parsedRate;
    }

    function toOptions(choices: GenericChoice[]) {
        return choices.map((choice) => ({ key: choice.key, value: String(choice.key), label: choice.value }));
    }

    return (
        <StyledCard tone="muted" padding="sm">
            {(scale.id !== initialScale.id || editing) && scales?.length > 1 && (
                <DeleteButton color={Colors.error} onClick={() => deleteScale(scale.id)} size="sm">
                    Delete
                </DeleteButton>
            )}
            <Stack gap="none">
                <Grid template="minmax(0, 128px) minmax(0, 1fr)" collapseAt="md" gap="md" align="start">
                    <FieldGroup label="Pay rate">
                        <Row gap="sm" align="center">
                            <Text weight="bold">$</Text>
                            <TextInput
                                value={rate}
                                onChange={setRate}
                                onBlur={() => update(coerceRate(rate), [], null, 'rate')}
                                fullWidth
                                color="white"
                            />
                            <Text tone="secondary">/hr</Text>
                        </Row>
                    </FieldGroup>
                    <Stack gap="sm">
                        <FieldGroup label="Education Requirement">
                            <Select
                                fullWidth
                                value={String(educationChoice(scale, qualifications.education) ?? '')}
                                onChange={(value) => update(Number(value), qualifications.education, null, 'education')}
                                options={toOptions(orderBy(qualifications.education, 'order'))}
                                tone="primary"
                            />
                        </FieldGroup>
                        <FieldGroup label="Years of Experience">
                            <Select
                                fullWidth
                                value={String(experienceChoice(scale, qualifications.experience) ?? '')}
                                onChange={(value) =>
                                    update(Number(value), qualifications.experience, null, 'experience')
                                }
                                options={toOptions(qualifications.experience)}
                                tone="primary"
                            />
                        </FieldGroup>
                        <Row justify="between" align="center" wrap>
                            <Text weight="bold">Additional Qualifications:</Text>
                            <Link onClick={() => setOpen(true)} size="sm">
                                + Add Qualifications
                            </Link>
                        </Row>
                        <AdditionalModal open={open} onClose={() => setOpen(false)} scale={scale} />
                        <AdditionalPills pills={pills} />
                    </Stack>
                </Grid>
                {problems?.rate === 'bad' && (
                    <Text size="xs" tone="error">
                        Please input a valid rate
                    </Text>
                )}
                {problems?.rate === 'lower' && (
                    <Text size="xs" tone="error">
                        Pay cannot be lower than previous rates
                    </Text>
                )}
                {problems?.rate === 'toolow' && (
                    <Text size="xs" tone="error">
                        The lowest pay allowed is ${MINIMUM_WAGE_BY_STATE[state as keyof typeof MINIMUM_WAGE_BY_STATE]}
                    </Text>
                )}
                {problems?.noQualifications && (
                    <Text size="xs" tone="error">
                        Please enter some qualifications for this pay rate
                    </Text>
                )}
                {problems?.duplicate && (
                    <Text size="xs" tone="error">
                        These pay rate qualifications are a duplicate of another, please change at least one
                        qualification
                    </Text>
                )}
            </Stack>
        </StyledCard>
    );
}

const DeleteButton = styled(Link)({
    position: 'absolute',
    top: 12,
    right: 12,
});

const StyledCard = styled(Card)({
    position: 'relative',
});
